import rdt from "web/script/embedded/reddit";
import environment from "web/script/modules/environment";
import { removeCurrencyChars } from "web/script/utils/currency-symbols";

/**
 * Handles all events from our analytics pipeline sent to the Reddit Pixel.
 *
 * There is one Reddit ad account (and therefore one Pixel ID) per region. The Reddit
 * click ID (`rdt_cid`) is appended to the landing URL by Reddit regardless of which
 * account served the ad, and the pixel sets its own `_rdt_uuid` first-party cookie —
 * so the only per-region difference is which Pixel ID we initialise. Region is chosen
 * from `environment.get("country")`, mirroring the Criteo / Kelkoo transports.
 *
 * There is one Pixel ID per region. The codebase uses the ISO 3166 alpha-2 code `GB`
 * for the UK (see `is_country_gb`), not `UK`.
 */
const PIXEL_IDS = {
    US: "a2_j175l0hto359",
    GB: "a2_j4lj58tsipov",
};
const SUPPORTED_COUNTRIES = new Set(Object.keys(PIXEL_IDS));

// Cap product arrays sent with feed events, mirroring the other transports.
const FEED_PRODUCT_ID_LIMIT = 5;

let cachedAnalytics = null;
let initialized = false;

function product(id) {
    return { id };
}

/**
 * Build the Reddit advanced-matching ("match keys") object passed to `rdt('init', …)`.
 *
 * Reddit recommends supplying user identifiers so events match to a Reddit account even
 * when the `_rdt_uuid` cookie / `rdt_cid` click ID are weak. Keys are only honoured on
 * the *first* event of the page and only via `init` (not `track`), so they are built
 * here and passed alongside the Pixel ID.
 *
 * `email` is the SHA-256 hash of the canonicalised user email, computed server-side and
 * exposed as `redditEmailHash` (see `reddit_email_hash` in `context_processors.py`). We
 * hash server-side — mirroring the Snapchat `getSnapchatHash()` pattern — rather than in
 * the browser, since no PII then reaches the pixel and there is no JS crypto dependency.
 *
 * `externalId` is our own device UID (`getDeviceUid()`), sent RAW — the same value
 * Facebook and RTB House use. Reddit recommends pairing the `externalId` with its
 * corresponding click ID; the device UID is present even for logged-out visitors (when
 * `redditEmailHash` is null) and is the value we can recover server-side alongside the
 * captured `rdt_cid`, so a later CAPI conversion can join the two.
 *
 * Only keys with a value are included. `phoneNumber` is not exposed to the frontend, and
 * `idfa`/`aaid` are mobile-app identifiers that do not exist for a web pixel, so neither
 * is sent.
 */
function matchKeys() {
    const keys = {};
    const emailHash = environment.get("redditEmailHash");
    if (emailHash) {
        keys.email = emailHash;
    }
    const deviceUid = cachedAnalytics && cachedAnalytics.getDeviceUid();
    if (deviceUid) {
        keys.externalId = deviceUid;
    }
    return keys;
}

/**
 * Build the metadata for a single-product conversion event. Shared by `Lead` and
 * `AddToCart` — the two events carry the same shape, just under different `customData`
 * field names. `conversionId` (the analytics event uuid) is a dedup key shared with any
 * later server-side CAPI event.
 *
 * The conversion value is `customData.sale_price` for a Lead (a plain numeric string,
 * computed server-side in USD — `get_discounted_price_for_buy_option(buy_option, "USD")`
 * in `track_lead.py` — so its currency defaults to `USD` since Lead's `customData` never
 * carries one) or `customData.price` for an AddToCart (a currency-symbol-formatted display
 * string, e.g. `"$695.00"`, paired with `customData.currency`) — `removeCurrencyChars`
 * strips any symbol before parsing either. `customData.price` is shared with several other
 * transports (Facebook, GA4, Snapchat, Pinterest, TikTok all read it too), so it's parsed
 * here rather than changed at the source. When neither value is present/parseable, the
 * event fires value-less, matching the other transports' behaviour.
 */
function mapProductConversion(d) {
    const metadata = {
        conversionId: d._uuid,
        products: [product(d.customData?.product_id)],
    };
    const rawValue = d.customData?.sale_price ?? d.customData?.price;
    const value = rawValue != null ? removeCurrencyChars(String(rawValue)) : NaN;
    if (!Number.isNaN(value)) {
        metadata.value = value;
        metadata.currency = d.customData?.currency ?? "USD";
    }
    return metadata;
}

/**
 * Map a single analytics event to zero or more Reddit pixel track calls.
 * Each entry is `[eventName, metadata]`.
 */
function mapEvent(evt) {
    const d = evt.data || {};
    const tracks = [];

    if (evt.type === "page_view") {
        // PageVisit fires on every page; specific events are layered on top.
        // conversionId = the page view's own id (same value as analytics.getPageViewId()),
        // a dedup key shared with the matching server-side CAPI event for the same page view.
        tracks.push(["PageVisit", { conversionId: d.pageview_id }]);

        if ((d.page_type === "product" || d.page_type === "product_overlay") && d.product_id) {
            tracks.push([
                "ViewContent",
                { conversionId: d.pageview_id, products: [product(d.product_id)] },
            ]);
        } else if (d.page_type === "search") {
            tracks.push(["Search", {}]);
        }
        return tracks;
    }

    if (evt.type === "event") {
        // Feed/search grid rendered with product IDs.
        if (d.category === "feed" && d.action === "view" && d.label === "feed_product_ids") {
            const ids = (d.customData?.ids || []).slice(0, FEED_PRODUCT_ID_LIMIT);
            if (ids.length) {
                // conversionId = d._uuid (the per-message id every analytics event gets, not
                // the page view's id): a single page view can render this event more than
                // once (e.g. infinite scroll), and each render is a distinct ViewContent.
                tracks.push(["ViewContent", { conversionId: d._uuid, products: ids.map(product) }]);
            }
        }
        // Add to cart / "Add to Bag". Matched on category+action only: the label varies
        // by context (`product_details` / `price_comparison` for native-checkout PDP adds,
        // `save_for_later_add_to_cart` when buying a saved item, `add_to_cart_button` for
        // affiliate/express — see `getAddToCartAnalyticsLabel` in checkout-buy-button.tsx),
        // so filtering on a single label would drop the most common add-to-bag events.
        else if (d.category === "cart" && d.action === "add_item") {
            tracks.push(["AddToCart", mapProductConversion(d)]);
        }
        // Wishlist.
        else if (d.category === "save_for_later" && d.action === "add_item") {
            tracks.push(["AddToWishlist", { products: [product(d.customData?.product_id)] }]);
        }
        // Affiliate lead / clickout conversion (excluding CSS leads). Client-side
        // conversion, attributed by Reddit via its own `_rdt_uuid` / `rdt_cid`.
        else if (
            d.category === "affiliate" &&
            (d.action === "lead" || d.action === "lead_sold_out") &&
            !d.customData?.isCssLead
        ) {
            tracks.push(["Lead", mapProductConversion(d)]);
        }
    }

    return tracks;
}

export default {
    init(analytics) {
        cachedAnalytics = analytics;
        initialized = false; // re-initialise across consent changes / test runs
    },
    send(data) {
        const country = environment.get("country");
        if (!SUPPORTED_COUNTRIES.has(country)) {
            return Promise.resolve();
        }

        const tracks = data.flatMap(mapEvent);
        if (!tracks.length) {
            return Promise.resolve();
        }

        if (!initialized) {
            rdt("init", PIXEL_IDS[country], matchKeys());
            initialized = true;
        }

        tracks.forEach(([name, metadata]) => rdt("track", name, metadata));
        return Promise.resolve();
    },
};
